import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import FormGroup from '../../core/components/FormGroup'
import {
  createCampaign,
  ICampaign,
  ICampaignPayload,
  updateCampaign,
} from '../../core/services/campaign'
import { MessageBox } from './messages/MessageBox'

export interface CreateCampaignModalProps {
  showEditModal: boolean
  campaign?: ICampaign
  setShowEditModal: (show: boolean) => void
  editMode: boolean
  onSaved: () => void
}

interface ICampaignValues {
  name: string
  url: string
  passwordProtected: boolean
  password?: string
  onlyForLoggedInUsers: boolean
  onlyForLoyalUsers: boolean
  active: boolean
}

const defaultValues: ICampaignValues = {
  name: '',
  url: '',
  passwordProtected: false,
  password: '',
  onlyForLoggedInUsers: false,
  onlyForLoyalUsers: false,
  active: false,
}

export const CreateCampaignModal = (
  props: CreateCampaignModalProps,
): JSX.Element => {
  const { showEditModal, setShowEditModal, campaign, editMode, onSaved } = props

  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const schema = yup.object().shape({
    name: yup.string().required('Field is required'),
    url: yup.string().required('Field is required'),
    passwordProtected: yup
      .boolean()
      .oneOf([true, false])
      .required()
      .default(false),
    password: yup.string().when('passwordProtected', {
      is: (val: boolean) => val,
      then: (s) => s.required('Field is required'),
      otherwise: (s) => s.optional(),
    }),
    onlyForLoggedInUsers: yup
      .boolean()
      .oneOf([true, false])
      .required()
      .default(false),
    onlyForLoyalUsers: yup
      .boolean()
      .oneOf([true, false])
      .required()
      .default(false),
    active: yup.boolean().oneOf([true, false]).required().default(false),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    watch,
  } = useForm<ICampaignValues>({
    mode: 'onChange',
    resolver: yupResolver<ICampaignValues>(schema),
    defaultValues,
  })

  const passwordProtectedWatch = watch('passwordProtected')

  useEffect(() => {
    if (campaign) {
      setValue('name', campaign.name, { shouldValidate: true })
      setValue('url', campaign.url, { shouldValidate: true })
      setValue('passwordProtected', campaign.passwordProtected)
      setValue('password', campaign.password ?? '')
      setValue('onlyForLoggedInUsers', campaign.onlyForLoggedInUsers)
      setValue('onlyForLoyalUsers', campaign.onlyForLoyalUsers)
      setValue('active', campaign.active)
    }
    return () => {
      reset(defaultValues)
    }
  }, [campaign, showEditModal])

  const handleClose = () => {
    setShowEditModal(false)
  }

  const showFeedback = (error: boolean, msg: string, status?: number) => {
    setIsError(error)
    setMessage(msg)
    setErrorStatus(status)
    setShowMessageBox(true)
  }

  const copyToClipboard = async (text: string, label: string, key?: string) => {
    if (!text) {
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      if (key) {
        setCopiedKey(key)
        setTimeout(() => {
          setCopiedKey((prev) => (prev === key ? null : prev))
        }, 2000)
      } else {
        showFeedback(false, `${label} copied`)
      }
    } catch {
      showFeedback(true, `Failed to copy ${label.toLowerCase()}`)
    }
  }

  const submit = async (values: ICampaignValues) => {
    const payload: ICampaignPayload = {
      name: values.name,
      url: values.url,
      passwordProtected: values.passwordProtected,
      password: values.passwordProtected ? values.password ?? '' : '',
      onlyForLoggedInUsers: values.onlyForLoggedInUsers,
      onlyForLoyalUsers: values.onlyForLoyalUsers,
      active: values.active,
    }

    try {
      setIsSaving(true)
      if (campaign?.id) {
        const { status } = await updateCampaign(campaign.id, payload)
        if (status === 200) {
          showFeedback(false, 'Update Successful')
          onSaved()
          handleClose()
        }
      } else {
        const { status } = await createCampaign(payload)
        if (status === 200) {
          showFeedback(false, 'Create Successful')
          onSaved()
          handleClose()
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        showFeedback(
          true,
          e.message,
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <Modal
        open={showEditModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="campaign"
              title={editMode ? 'Edit Campaign' : 'Add New Campaign'}
              subtitle="Campaigns"
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2 border-t border-monochrome-120 pt-4">
            {editMode && campaign ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    copyToClipboard(
                      campaign.deepLinkUrl,
                      'Web link',
                      `${campaign.id}:web`,
                    )
                  }
                  className={
                    copiedKey === `${campaign.id}:web`
                      ? '!bg-green-600 !border-green-600 !text-white hover:!bg-green-600'
                      : ''
                  }
                >
                  {copiedKey === `${campaign.id}:web`
                    ? 'Copied'
                    : 'Copy Web Link'}
                  {copiedKey !== `${campaign.id}:web` && (
                    <Icon
                      icon="copy"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    copyToClipboard(
                      campaign.mobileUrl,
                      'Mobile link',
                      `${campaign.id}:mobile`,
                    )
                  }
                  className={
                    copiedKey === `${campaign.id}:mobile`
                      ? '!bg-green-600 !border-green-600 !text-white hover:!bg-green-600'
                      : ''
                  }
                >
                  {copiedKey === `${campaign.id}:mobile`
                    ? 'Copied'
                    : 'Copy Mobile Link'}
                  {copiedKey !== `${campaign.id}:mobile` && (
                    <Icon
                      icon="copy"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    />
                  )}
                </Button>
              </div>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={isSaving}
                form="campaign-form"
                type="submit"
                variant="primary"
                loading={isSaving}
              >
                {campaign?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="campaign-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-5"
        >
          <FormGroup label="Campaign Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Campaign Name"
            />
          </FormGroup>

          <FormGroup label="URL (must be unique)" error={errors?.url?.message}>
            <Input
              isInvalid={!!errors?.url?.message}
              {...register('url')}
              type="text"
              placeholder="Enter URL"
            />
          </FormGroup>

          <FormGroup
            label="User must use a password"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'passwordProtected',
            }}
          >
            <Controller
              name="passwordProtected"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="passwordProtected"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>

          {passwordProtectedWatch && (
            <>
              <FormGroup label="Password" error={errors?.password?.message}>
                <Input
                  isInvalid={!!errors?.password?.message}
                  {...register('password')}
                  type="text"
                  placeholder="Enter Password"
                />
              </FormGroup>
              <div className="h-[1px] bg-monochrome-120" />
            </>
          )}

          <FormGroup
            label="User have to be logged in"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'onlyForLoggedInUsers',
            }}
          >
            <Controller
              name="onlyForLoggedInUsers"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="onlyForLoggedInUsers"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>

          <FormGroup
            label="Loyal users"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'onlyForLoyalUsers',
            }}
          >
            <Controller
              name="onlyForLoyalUsers"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="onlyForLoyalUsers"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>

          <FormGroup
            label="Active"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'active',
            }}
          >
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="active"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
